/**
 * EnumSelect - 动态枚举下拉选择组件
 *
 * 根据枚举分类编码自动加载选项，支持缓存、加载状态和自定义样式。
 */

import React from 'react';
import { Select, Spin } from 'antd';
import type { SelectProps } from 'antd';
import { useEnumOptions } from '../hooks/useEnumOptions';

interface EnumSelectProps extends Omit<SelectProps, 'options' | 'loading'> {
  /** 枚举分类编码，如 'CASE_STATUS', 'EXPENSE_TYPE' */
  categoryCode: string;
  /** 是否显示带颜色的选项 */
  showColor?: boolean;
  /** 挂载时是否强制刷新（绕过缓存，确保配置中心最新改动立即可见） */
  forceRefresh?: boolean;
}

export function EnumSelect({
  categoryCode,
  showColor = false,
  forceRefresh = false,
  placeholder = '请选择',
  allowClear = true,
  disabled,
  value,
  onChange,
  style,
  className,
  ...restProps
}: EnumSelectProps) {
  const { options, loading, error, refresh } = useEnumOptions(categoryCode);

  // 挂载时按需强制刷新，绕过前端缓存
  React.useEffect(() => {
    if (forceRefresh) {
      refresh(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryCode, forceRefresh]);

  // 过滤禁用的选项（API 已返回过滤后的数据，此处无需额外过滤）
  const filteredOptions = options;

  // 转换为 Select options 格式
  const selectOptions = filteredOptions.map(opt => ({
    value: opt.value,
    label: showColor && opt.color ? (
      <span style={{ color: getColorStyle(opt.color) }}>{opt.label}</span>
    ) : opt.label,
  }));

  // 加载状态
  const notFoundContent = loading ? (
    <Spin size="small" />
  ) : error ? (
    <span style={{ color: '#ff4d4f' }}>加载失败</span>
  ) : (
    '暂无数据'
  );

  return (
    <Select
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      allowClear={allowClear}
      disabled={disabled || loading}
      loading={loading}
      options={selectOptions}
      notFoundContent={notFoundContent}
      style={style}
      className={className}
      showSearch
      filterOption={(input, option) =>
        (option?.label?.toString() || '').toLowerCase().includes(input.toLowerCase())
      }
      {...restProps}
    />
  );
}

/**
 * 根据颜色名称获取实际颜色值
 */
function getColorStyle(color: string | undefined): string {
  const colorMap: Record<string, string> = {
    success: '#52c41a',
    warning: '#faad14',
    error: '#ff4d4f',
    processing: '#2B5298',
    default: '#666666',
  };
  return colorMap[color || 'default'] || '#666666';
}

export default EnumSelect;